import type { Messages } from './messages';

/** Japanese copy. Schema and ordering follow the Korean tree in `messages.ts`. */
export const ja: Messages = {
  meta: {
    documentTitle: 'エコテラリウム (Eco Terrarium: Micro Evolution) - 生態系の指揮者',
  },
  common: {
    close: '閉じる',
    cancel: 'キャンセル',
  },
  units: {
    duration: (minutes: number, seconds: number) => `${minutes}分${seconds < 10 ? '0' : ''}${seconds}秒`,
    seconds: (value: number) => `${value}秒`,
    organisms: (count: number) => `${count}匹`,
    totalOrganisms: (count: number) => `合計${count}体`,
    speciesCount: (count: number) => `${count}種`,
    generation: (value: number) => `第${value}世代`,
  },
  language: {
    groupLabel: '言語選択',
    optionLabel: (name: string) => `${name}`,
  },
  topbar: {
    title: 'エコテラリウム',
    subtitle: 'ガラス瓶の中の生態系のバランスを観察し、調整しましょう',
    navLabel: '補助メニュー',
    judgeShort: 'ツアー',
    judgeFull: '審査員クイックツアー',
    encyclopedia: '図鑑',
    encyclopediaTitle: '生物図鑑を開く',
    quests: 'クエスト',
    questsTitle: 'クエスト一覧を開く',
    customization: 'カスタム',
    customizationTitle: 'テラリウムのデザインをカスタマイズ',
    photo: 'スナップ',
    photoTitle: 'フォトモード & スナップショット',
    hive: 'Hive共有',
    hiveTitle: 'Com2uS Hive ソーシャル共有',
    qr: 'QR',
    qrTitle: 'スマホですぐプレイ (QRコード)',
    helpTitle: 'ゲームガイド',
  },
  discovery: {
    kicker: '新しい生物種を記録しました',
    action: '図鑑を確認',
  },
  dna: {
    defaultCreatorName: '生態系の指揮者',
    defaultTerrariumName: 'わたしのエコテラリウム',
  },
  startScreen: {
    kicker: 'MICRO EVOLUTION SIMULATOR',
    title: 'エコテラリウム',
    tagline: '小さなガラス瓶の中で、いのちのバランスを指揮しよう。',
    description:
      '光・水分・温度・栄養を調整して、生産者から捕食者まで連なる食物連鎖を育てるヒーリング生態系シミュレーションです。',
    objectiveTitle: 'ゲームの目的',
    objectiveBody:
      'どれか一種が増えすぎたり絶滅したりしないように管理しながら、新種とより高いバイオハーモニーを見つけましょう。',
    playTitle: '遊びかた',
    steps: [
      { label: '光と水分', description: '植物が育つ環境をつくりましょう。' },
      { label: '栄養と触媒', description: 'ガラス瓶にエサを落としましょう。' },
      { label: '温度とバランス', description: '変化に適応する生態系を見守りましょう。' },
    ],
    footnote: '捕食と摂食は、ガラス瓶の中で光る粒子のエフェクトで確認できます。',
    action: 'テラリウムを始める',
  },
  hud: {
    environmentSectionLabel: '環境操作',
    toolGroupLabel: 'ガラス瓶ツールの選択',
    tools: {
      inspect: {
        button: '顕微鏡観察',
        buttonTitle: '微生物をクリックして顕微鏡で観察し、遺伝子を分析します',
        guideTitle: '顕微鏡レンズ',
        guideDetail: '光る生物をクリックして、名前・世代・エネルギー・遺伝子をじっくり観察しましょう。',
      },
      feed: {
        button: '栄養投下',
        buttonTitle: 'ガラス瓶の中をクリックして有機栄養剤のエサを投下します',
        guideTitle: '栄養剤スポイト',
        guideDetail: 'エネルギーが不足した生物の近くに少しずつ落としましょう。入れすぎるとバランスが崩れます。',
      },
      mutagen: {
        button: '変異触媒',
        buttonTitle: '突然変異触媒を注入して急激な遺伝変異を誘発します',
        guideTitle: '突然変異触媒',
        guideDetail: '特別な進化を狙うときだけ使いましょう。高温環境と組み合わせると変化がより劇的になります。',
      },
      tap: {
        button: '瓶ノック',
        buttonTitle: 'ガラス瓶をコンコン叩いて衝撃波を発生させます',
        guideTitle: 'ガラス瓶ノック',
        guideDetail: '短い衝撃波で生物の動きを観察できます。叩きすぎるとストレスを与えてしまいます。',
      },
    },
    dayNightLabel: '昼夜サイクル',
    dayNightTitle: '昼夜の自動サイクルを切り替え',
    speedGroupLabel: 'シミュレーション速度',
    play: 'シミュレーションを再生',
    pause: 'シミュレーションを一時停止',
    playPauseTitle: 'シミュレーションの一時停止 / 再生',
    unmute: 'オーディオをオン',
    mute: 'オーディオをオフ',
    muteTitle: 'プロシージャルBGMのミュート切り替え',
    microscopeHowToTitle: '顕微鏡観察の手順',
    microscopeHowToCompact: 'ガラス瓶の中の生物を選ぶと、個体情報をすぐに確認できます。',
    microscopeSteps: [
      '「顕微鏡観察」ツールを選びます。',
      'ガラス瓶の中で光る生物をクリック、またはタップします。',
      '名前・年齢・世代・遺伝子を観察ウィンドウで確認します。',
    ],
    sliders: {
      sunlight: {
        label: '日照量 (Sunlight)',
        aria: '日照量',
        low: '暗い',
        mid: '光合成が活発',
        high: '灼熱',
      },
      moisture: {
        label: '水分 / 降雨 (Rain)',
        aria: '水分と降雨',
        low: '乾燥',
        mid: 'しっとり',
        high: '豪雨',
      },
      temperature: {
        label: '温度 (Temperature)',
        aria: '温度',
        low: '-10°C (氷河期)',
        mid: '22°C (温帯)',
        high: '45°C (熱帯)',
      },
      nutrients: {
        label: '栄養塩類 (Nutrients)',
        aria: '栄養塩類',
        low: '痩せた土',
        mid: '肥沃',
        high: '富栄養',
      },
    },
  },
  canvas: {
    inspectAria: '顕微鏡観察: ガラス瓶の中の生物をクリックまたはタップすると観察ウィンドウが開きます',
    defaultAria: 'エコテラリウムのガラス瓶',
    hints: {
      inspect: '生物をクリックまたはタップして観察ウィンドウを開きましょう',
      feed: 'ガラス瓶の中を選んで栄養剤を投下しましょう',
      mutagen: 'ガラス瓶の中を選んで触媒を投下しましょう',
      tap: 'ガラス瓶を選んで衝撃波を起こしましょう',
    },
  },
  advisor: {
    sectionLabel: '生態系の状態と次のアドバイス',
    title: '生態系アドバイス',
    subtitle: '健全度と現在の環境をあわせて読み取ります',
    healthAria: (health: number) => `生態系の健全度 ${health}%`,
    readingsLabel: '環境反応のまとめ',
    readingNames: {
      sunlight: '光',
      moisture: '水分',
      temperature: '温度',
    },
    readings: {
      'sunlight.low': { label: '光量不足', detail: '光合成が遅くなります' },
      'sunlight.high': { label: '強い直射光', detail: '過熱とアオコに注意しましょう' },
      'sunlight.balanced': { label: 'やわらかな採光', detail: '生産者が安定して育ちます' },
      'moisture.low': { label: '乾燥注意', detail: '水分ストレスが強まります' },
      'moisture.high': { label: '豪雨状態', detail: '過湿と濁りに注意しましょう' },
      'moisture.balanced': { label: 'しっとりした循環', detail: '分解と水生活動が活発です' },
      'temperature.cold': { label: '低温警報', detail: '霜が降り、代謝が鈍ります' },
      'temperature.hot': { label: '高温警報', detail: '熱気と蒸気が生物の負担になります' },
      'temperature.pressure': { label: '適応圧力', detail: '一部の生物が温度ストレスを受けています' },
      'temperature.balanced': { label: '温帯で安定', detail: 'ほとんどの生物が快適な温度です' },
    },
    advice: {
      recover: {
        title: '回復が必要です',
        detail: 'まず日照量45〜70%、水分50〜80%、温度18〜26°Cに戻し、栄養剤を一度投下してみましょう。',
      },
      temperature: {
        title: 'まずは温度をやわらげましょう',
        detail: '極端な温度はエネルギー消費を増やします。適応進化を狙っていないなら22°C前後が安全です。',
      },
      'moisture.low': {
        title: '水分バランスを整えましょう',
        detail: '雨を降らせて水分を50%以上に上げてみましょう。',
      },
      'moisture.high': {
        title: '水分バランスを整えましょう',
        detail: '降雨を減らして80%未満に下げ、水の濁りを防ぎましょう。',
      },
      'sunlight.low': {
        title: '光を整えるタイミングです',
        detail: '日照量を少し上げて生産者にエネルギーを届けましょう。',
      },
      'sunlight.high': {
        title: '光を整えるタイミングです',
        detail: '直射光を70%前後まで下げて過熱を避けましょう。',
      },
      diversity: {
        title: '多様性を育てましょう',
        detail: '栄養剤を一度投下したあと、食物連鎖の4階層の個体数が一緒に保たれるか観察してみましょう。',
      },
      observe: {
        title: '今は観察に向いています',
        detail: '生態系は安定しています。顕微鏡観察で世代と遺伝子を眺めたり、小さな環境変化を試したりしてみましょう。',
      },
    },
  },
  stats: {
    sectionLabel: '生態系の観測指標',
    health: '生態系の健全度',
    harmony: 'バイオハーモニー',
    foodWeb: '食物連鎖の個体数',
    trophic: {
      producer: '生産者',
      herbivore: '一次消費者',
      predator: '捕食者',
      decomposer: '分解者',
    },
    chartTitle: 'ロトカ・ヴォルテラ動力学の波',
    chartLegend: {
      producers: '植物',
      herbivores: '草食',
      predators: '捕食',
    },
    chartEmpty: 'シミュレーションデータを蓄積中...',
    biodiversity: "生物多様性 H':",
    unlocked: '図鑑の解放:',
    survival: '生存の継続:',
    extinction: '累積の死滅/循環:',
  },
  inspector: {
    dialogLabel: '個体観察ウィンドウ',
    unknownName: '微生物',
    unknownSpecies: '未確認生物',
    saveName: '名前を保存',
    editName: '名前を編集',
    energy: 'エネルギー / 体力',
    age: '年齢 / 寿命',
    genomeTitle: '遺伝子分析 (Genome DNA)',
    genomeDimensions: '10次元の表現型ベクトル',
    genome: {
      size: 'サイズ係数 (Size)',
      speed: '遊泳速度 (Speed)',
      tempOpt: '最適温度',
      moistOpt: '最適水分',
      defense: '外殻の防御力',
      mutationRate: '突然変異率',
      metabolism: '代謝効率 (Metabolism)',
      hue: '体色の色相 (Hue)',
      bioluminescence: '生物発光 (Biolum.)',
    },
    injectMutagen: 'この個体に突然変異触媒を注入する',
  },
  encyclopedia: {
    dialogLabel: '仮想生物図鑑',
    title: '仮想生物図鑑 (Encyclopedia)',
    progress: (unlocked: number, total: number, percent: number) => `${unlocked} / ${total} 発見 (${percent}%)`,
    subtitle: '小さなガラス瓶の中で生まれる16種の微生物、その進化系統と秘密',
    filters: {
      all: 'すべて (All)',
      producer: '生産者 (Producers)',
      herbivore: '一次消費者 (Herbivores)',
      predator: '捕食者 (Predators)',
      decomposer: '分解者 (Decomposers)',
    },
    lockedName: '??? 未確認生物',
    lockedScientific: '条件を満たすと進化',
    lockedTitle: 'まだ発見されていない未確認種',
    lockedBody: 'テラリウムの環境を調整して、進化の条件を満たしてみましょう。',
    loreTitle: '生態観察記録 (Lore)',
    traitsTitle: '固有の生体形質',
    traits: {
      tempOpt: '最適温度',
      moistOpt: '最適水分',
      speed: '移動速度',
      bioluminescence: '生物発光度',
    },
    hintTitle: '進化のヒント (Evolution Hint)',
  },
  quests: {
    dialogLabel: '生態系の指揮者クエスト',
    title: '生態系の指揮者クエスト (Quests)',
    progress: (completed: number, total: number, percent: number) => `${completed} / ${total} 達成 (${percent}%)`,
    subtitle: '目標を達成して、限定のテラリウムパーツと試薬を解放しましょう',
    reward: (title: string) => `報酬: ${title}`,
    completed: '達成済み',
    inProgress: '進行中',
  },
  customization: {
    dialogLabel: 'テラリウムのデザインカスタム',
    title: 'テラリウムのデザインカスタム (Customization)',
    subtitle: 'ガラス瓶の形、底の土、背景の環境を自由に飾りましょう',
    bottleTitle: 'ガラス瓶の外形 (Bottle Shape)',
    substrateTitle: '底の土テーマ (Substrate Theme)',
    backgroundTitle: '背景の環境テーマ (Background Theme)',
    bottles: {
      'classic-jar': { name: 'クラシックボトル', desc: 'やわらかな曲線の基本の円筒ガラス瓶' },
      'geometric-dome': { name: 'ジオメトリックドーム', desc: 'ダイヤモンドカットのガラスドーム' },
      'antique-flask': { name: 'アンティークフラスコ', desc: '神秘的な錬金術師の丸フラスコ' },
      'crystal-sphere': { name: '魔法の水晶球', desc: '空中に浮かぶ幻想的な球体' },
    },
    substrates: {
      'moss-forest': { name: '苔の森 (Moss Forest)', desc: '生命力あふれる青い苔と土' },
      'deep-sea-sand': { name: '深海サンゴ砂 (Deep Sea)', desc: '青く輝く海底の砂' },
      'volcanic-obsidian': { name: '火山黒曜石 (Volcanic)', desc: 'マグマの熱を宿す黒曜石の岩盤' },
      'crystal-cave': { name: 'アメジスト洞窟 (Crystal Cave)', desc: '紫の水晶がそびえる岩石地帯' },
    },
    backgrounds: {
      'cozy-lab': { name: '居心地のよい研究室', desc: '温かみのある書斎と灯り' },
      'dawn-mist': { name: '夜明けの霧の森', desc: '爽やかなフィトンチッドと朝の陽射し' },
      'sunset-window': { name: '夕陽の窓辺', desc: '赤く染まる黄昏の光' },
      'cosmic-aurora': { name: 'オーロラの宇宙', desc: '天の川と踊る極光' },
    },
  },
  photo: {
    dialogLabel: 'フォトモードとスナップショット',
    title: 'フォトモード & スナップショット (Photo Mode)',
    subtitle: '美しいテラリウムの瞬間を、雰囲気のあるフィルターとともに残しましょう',
    loading: 'キャプチャを読み込み中...',
    filtersTitle: 'フィルター選択 (Filters)',
    filters: {
      original: 'ナチュラル',
      vintage: 'ヴィンテージフィルム',
      neon: 'サイバーネオン',
      pastel: 'ドリームパステル',
      noir: 'クラシックモノクロ',
    },
    download: '高画質スナップショットを保存 (PNG)',
  },
  help: {
    dialogLabel: 'エコテラリウム プレイガイド',
    title: 'エコテラリウム プレイガイド (How to Play)',
    subtitle: '小さなガラス瓶の生態系の指揮者になる方法',
    loopTitle: '1. 生態系の4つの循環リング (Core Loop)',
    loopBody: 'ガラス瓶の中では4階層の生命が共存し、絶えず物質とエネルギーを循環させています:',
    loopRoles: {
      producer: '光/水分で光合成',
      herbivore: '植物を食べて排泄',
      predator: '草食生物を狩る',
      decomposer: '死骸を養分へ',
    },
    toolsTitle: '2. 環境調整と神の手ツール (God Tools)',
    tools: {
      sunlight: { name: '日照量', desc: '生産者の光合成を促進し、温度をわずかに上げます。' },
      moisture: { name: '水分/降雨', desc: '水滴が結び、水生生物と分解者が活性化します。' },
      temperature: { name: '温度調整', desc: '氷河期(-10°C)から温暖期(45°C)まで突然変異の圧力をかけます。' },
      tap: { name: 'ガラス瓶ノック', desc: '瓶をコンコン叩いて生物の群れを散らします。' },
    },
    speciationTitle: '3. 16種の生物図鑑 & プロシージャル・バイオサウンド',
    speciationBody:
      '極端な環境変化や突然変異触媒の注入によって、16種の個性的な新種が誕生します。生態系の多様性が高まるほど、Web Audio APIがリアルタイムでより豊かなバイオ和音BGMを生み出します!',
    saveTitle: '4. 自動セーブ & リセット',
    saveBody:
      'テラリウムとクエストの進捗はこのブラウザに自動保存され、ウィンドウを閉じても続きから再開できます。最初からやり直したい場合は下からリセットできます。図鑑の解放とクエストの記録も一緒に消えます。',
    resetWarning: '本当にリセットしますか? いまの生態系は元に戻せません。',
    resetConfirm: 'リセットする',
    resetTrigger: 'テラリウムを最初からやり直す',
  },
  hive: {
    dialogLabel: 'Hive共有と訪問',
    title: 'Com2uS Hive ソーシャルプラットフォーム',
    subtitle: 'テラリウムDNAコードで共有し、ほかの指揮者の生態系を訪ねましょう',
    tabs: {
      share: 'テラリウムを共有',
      import: 'コードを読み込む',
      visitor: '仮想訪問モード',
      leaderboard: 'グローバルランキング',
    },
    referenceId: 'テラリウム参照ID',
    dnaValid: '生態系DNAは有効です',
    copyCode: '参照IDをコピー',
    copyLink: 'リンクをコピー',
    copied: 'コピーしました!',
    referenceNote:
      '参照IDはリンクを見分けるための指紋です。生態系全体を復元するには、下の完全なDNAリンクを共有してください。',
    deepLink: 'ワンクリック・ディープリンク (Direct Web Link)',
    snapshot: {
      discovered: '発見した生物',
      harmony: 'ハーモニースコア',
      population: '現在の個体数',
    },
    importIntro:
      '友だちが共有した完全なDNAリンク、または圧縮DNA文字列を入力すると、環境・図鑑の解放・個体分布・遺伝子プールがまとめて復元されます。',
    importPlaceholder: '完全なDNAリンク、または圧縮DNA文字列を貼り付けてください...',
    importEmptyError: 'コードまたはURLを入力してください。',
    importInvalidError: '有効なテラリウムDNAコードではありません。コードをもう一度確認してください。',
    importAction: 'テラリウムの生態系を読み込む',
    visitor: {
      featured: 'おすすめの人気テラリウム: ねこの苔の森',
      featuredOwner: '指揮者: HIDEA_Kim (『ねことスープ』公式テーマ)',
      giftPollen: '花粉をプレゼント',
      giftPollenDone: 'プレゼント完了!',
      giftPollenDesc: '相手に栄養バフを付与',
      harvestSpore: 'オーロラフィンの胞子を採取',
      harvestSporeDone: '採取完了!',
      harvestSporeDesc: '自分のテラリウムへ移植',
    },
    leaderboard: [
      { playerName: '指揮者_ミシェル', terrariumName: 'オーロラ星雲の庭', badge: 'グランドマエストロ' },
      { playerName: 'ねことスープ好き', terrariumName: 'ぷるぷるゼリーボトル', badge: 'ヒーリングの守護者' },
      { playerName: 'ミントロケット_デイブ', terrariumName: '深海クラーケン生態系', badge: '深海の探検家' },
      { playerName: 'バイオ_コーデックス', terrariumName: '極低温クリスタルドーム', badge: '氷河期の開拓者' },
    ],
  },
  qr: {
    dialogLabel: 'スマホですぐプレイ',
    title: 'スマホですぐプレイ',
    subtitle: 'カメラでQRを映すと、インストールなしでブラウザからそのまま開きます',
    scanHint: 'iPhone・Androidの標準カメラを向けるだけです',
    urlLabel: 'プレイ用アドレス',
    copyLink: 'アドレスをコピー',
    copied: 'コピーしました!',
    localNotice: '今表示している画面はローカルアドレスでスマホからは開けないため、QRは公開アドレスを指しています。',
  },
  showcase: {
    dialogLabel: '審査員専用ショーケース',
    title: '審査員専用ショーケース & Codex 開発記',
    subtitle: '1分でわかる主要機能のクイックツアープリセットと、OpenAI Codex 協働アーキテクチャ',
    tabPresets: '1分クイックショーケース (Quick Presets)',
    tabCodex: 'OpenAI Codex AI 開発記 (Dev Story)',
    presetApplied: (name: string) => `「${name}」プリセットを適用しました!`,
    applyPreset: 'プリセットを適用',
    presets: {
      prosperity: {
        name: '即時繁栄モード',
        heading: '01. 即時繁栄モード (Full Prosperity)',
        caption: '生態系の黄金期を演出',
        body: '4階層の栄養段階(植物・草食・捕食・分解者)が完璧なバランスで優雅に泳ぐ、最適な生態系をその場で構築します。',
      },
      mutation: {
        name: '突然変異の加速',
        heading: '突然変異の加速 (Mutation Burst)',
        caption: '新種の進化イベントを誘発',
        body: '温度を35°Cまで上げ、大量の突然変異触媒を注入して、5秒以内にオーロラフィンやグロウテイルなどの希少変種の誕生を実演します。',
      },
      iceage: {
        name: '氷河期からの脱出',
        heading: '氷河期からの脱出 (Ice Age)',
        caption: '極低温への適応シミュレーション',
        body: '温度を-5°Cまで急冷してガラス瓶に霜を結ばせ、極寒のなかでクリスタルリーフと低温耐性種が適応していく過程を観察します。',
      },
      symphony: {
        name: 'ハーモニー・オーケストラ',
        heading: 'ハーモニー・オーケストラ (Full Symphony)',
        caption: 'Web Audio サウンドスケープの試聴',
        body: '生態系の健全度を100%に設定し、アンビエントパッド、ペンタトニックのバイオチャイム、雨音のポリが重なる和音の極致を聴きます。',
      },
    },
    codex: {
      whereTitle: '1. Codex の活用領域 (Where)',
      whereItems: [
        {
          term: '数理生態系モデル',
          body: 'ロトカ・ヴォルテラの4元連立微分方程式を4次ルンゲ=クッタ(RK4)で解く数値解析モジュールの設計。',
        },
        {
          term: 'プロシージャル・サウンドシンセ',
          body: '外部MP3に依存しない、Web Audio API のコードのみで組んだペンタトニック適応和音ジェネレーター。',
        },
        {
          term: '遺伝流体シミュレーション',
          body: '10次元の表現型ベクトルのドリフトと、Boids 群れ行動の最適化。',
        },
      ],
      problemTitle: '2. Codex との技術課題の解決 (Problem Solving)',
      problemItems: [
        {
          term: '微分方程式の発散の抑制',
          body: '個体数が0へ急減したり無限に増大したりする非線形振動の問題を、環境緩衝係数(Environmental Buffer Multiplier)の導入によって数学的に安定化。',
        },
        {
          term: '60FPS のガベージコレクション最適化',
          body: '毎フレーム数百個のパーティクル生成で生じるGCスパイクを、エンティティプーリングとキャンバス描画のバッチ処理で解消。',
        },
      ],
      decisionTitle: '3. 人間クリエイターの芸術的・企画的な決断 (Human Decision)',
      decisionBody:
        '単なる数字の羅列のシミュレーションにとどまらず、キム・ドンギュ代表の『ねことスープ』のような温かいヒーリング体験と、キム・デフォン代表の『ミントロケット』らしい新鮮なサンドボックスの発見の楽しさを組み合わせ、独創的な作品へと昇華させました。',
    },
  },
};
